import React from "react";
import { Link } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { Modal, Badge, PrimaryButton } from "../../UI/DesignStandards";
import { tournamentPath } from "../../../utility/tournamentUrls";
import { useTournamentSlug } from "../../../context/TournamentRouteContext";

export const AllEventDetailsModal = ({ isOpen, onClose, allEventData }) => {
  const tournamentSlug = useTournamentSlug();
  const { eventName, bowlerName, allEventMetadata, totalScore, tournamentId } =
    allEventData || {};
  const {
    isComplete,
    primaryEventDetails,
    presentPrimaryEventCount,
    requiredPrimaryEventCount,
    calculationType,
  } = allEventMetadata || {};

  // Determine if this is a scratch or handicap all event
  const isScratchAllEvent = calculationType === "allEventScratch";
  const isHandicapAllEvent = calculationType === "allEventHdcp";

  // Format calculation type for display
  const getCalculationTypeLabel = () => {
    if (isScratchAllEvent) return "Scratch";
    if (isHandicapAllEvent) return "Handicap";
    return "Scratch"; // Default fallback
  };

  if (!allEventData) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={eventName}
      subtitle={bowlerName}
      footer={
        <PrimaryButton onClick={onClose} className="w-full">
          Close
        </PrimaryButton>
      }
    >
      {/* Total Score */}
      <div className="mb-6 p-4 card-inset">
        <div className="text-xs text-brand-muted mb-1 font-medium">
          Total All Event Score - {getCalculationTypeLabel()}
        </div>
        <div className="text-3xl font-score font-bold text-brand-charcoal">
          {totalScore?.toLocaleString() || 0}
        </div>
        {!isComplete && (
          <div className="text-xs text-brand-muted mt-1 italic">
            (Partial - {presentPrimaryEventCount} of {requiredPrimaryEventCount}{" "}
            events)
          </div>
        )}
      </div>

      {/* Status Badge */}
      <div className="mb-4">
        {isComplete ? (
          <Badge
            variant="default"
            className="text-sm !rounded-lg !bg-brand-charcoal !text-brand-cream"
          >
            <FaCheckCircle className="w-4 h-4 mr-2" />
            Complete ({presentPrimaryEventCount}/{requiredPrimaryEventCount}{" "}
            events)
          </Badge>
        ) : (
          <Badge
            variant="default"
            className="text-sm !rounded-lg !bg-brand-cream !text-brand-charcoal border border-brand-charcoal/20"
          >
            <FaTimesCircle className="w-4 h-4 mr-2" />
            Incomplete ({presentPrimaryEventCount}/{requiredPrimaryEventCount}{" "}
            events)
          </Badge>
        )}
      </div>

      {/* Primary Events Breakdown */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-brand-charcoal mb-3">
          Primary Event Scores ({getCalculationTypeLabel()}):
        </h3>
        {primaryEventDetails?.map((detail, index) => (
          <div
            key={detail.eventId || index}
            className="flex items-center justify-between p-3 card-inset"
          >
            <div className="flex items-center flex-1 min-w-0">
              {detail.isPresent ? (
                <FaCheckCircle className="w-5 h-5 text-brand-charcoal mr-3 flex-shrink-0" />
              ) : (
                <FaTimesCircle className="w-5 h-5 text-brand-muted mr-3 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                {detail.isPresent && detail.teamId && tournamentId ? (
                  <Link
                    to={`${tournamentPath(tournamentSlug || tournamentId, "/scoring")}?team=${detail.teamId}`}
                    className="text-sm font-medium text-brand-gold-ink hover:underline truncate block"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(); // Close modal when navigating
                    }}
                    aria-label={`View scoring for ${detail.eventName}`}
                    title={`View scoring for ${detail.eventName}`}
                  >
                    {detail.eventName}
                  </Link>
                ) : (
                  <div className="text-sm font-medium text-brand-charcoal truncate">
                    {detail.eventName}
                  </div>
                )}
              </div>
            </div>
            <div className="ml-3 text-right flex-shrink-0">
              {detail.isPresent ? (
                <div className="text-sm font-score font-semibold text-brand-charcoal">
                  {/* Show only the score that matches the All Event's calculation type */}
                  {isHandicapAllEvent
                    ? detail.handicapTotal?.toLocaleString() || 0
                    : detail.scratchTotal?.toLocaleString() || 0}
                </div>
              ) : (
                <span className="text-sm text-brand-muted italic">Missing</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};
